<template>
  <b-modal :active="true" has-modal-card trap-focus @close="$emit('close')">
    <div class="modal-card nt-editor-modal-card">
      <header class="modal-card-head">
        <p class="modal-card-title">
          {{
            isEditMode
              ? $t('admin_page.edit_notification_template')
              : $t('admin_page.add_notification_template')
          }}
        </p>
        <button
          type="button"
          class="delete"
          aria-label="close"
          @click="$emit('close')"
        />
      </header>

      <section class="modal-card-body">
        <b-field :label="$t('admin_page.template_name')">
          <b-input v-model.trim="form.name" maxlength="120" />
        </b-field>

        <b-field :label="$t('admin_page.template_subject')">
          <b-input
            ref="subjectInput"
            v-model.trim="form.subjectTemplate"
            maxlength="200"
            @focus="setActiveEditorTarget('subject')"
            @click.native="captureSubjectCursor"
            @keyup.native="captureSubjectCursor"
          />
        </b-field>

        <rich-text-editor
          ref="templateBodyEditor"
          v-model="form.bodyTemplate"
          :lable="$t('admin_page.template_body')"
          :headings="[1, 2, 3]"
          @onFocus="setActiveEditorTarget('body')"
        />

        <b-field :label="$t('admin_page.template_placeholders_help')">
          <div class="nt-placeholders">
            <b-tag
              v-for="placeholder in recommendedPlaceholders"
              :key="placeholder"
              type="is-light"
              class="nt-placeholder-tag"
              tabindex="0"
              role="button"
              @click.native="insertPlaceholder(placeholder)"
              @keydown.enter.native.prevent="insertPlaceholder(placeholder)"
            >
              {{ placeholder }}
            </b-tag>
          </div>
        </b-field>

        <b-field grouped>
          <b-checkbox v-model="form.isActive">
            {{ $t('admin_page.template_active') }}
          </b-checkbox>
        </b-field>
      </section>

      <footer class="modal-card-foot">
        <b-button @click="$emit('close')">{{ $t('cancel') }}</b-button>
        <b-button type="is-primary" :loading="isSaving" @click="save">
          {{ $t('save') }}
        </b-button>
      </footer>
    </div>
  </b-modal>
</template>

<script>
import RichTextEditor from '../../RichTextEditor';

export default {
  name: 'notification-template-editor-modal',
  components: {
    RichTextEditor,
  },
  props: {
    template: {
      type: Object,
      default: null,
    },
    isSaving: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      form: {
        _id: null,
        name: '',
        subjectTemplate: '',
        bodyTemplate: '',
        placeholders: [],
        isActive: true,
      },
      activeEditorTarget: 'body',
      subjectCursorPosition: 0,
      recommendedPlaceholders: [
        '{{jobTitle}}',
        '{{workOrderName}}',
        '{{workOrderNumber}}',
        '{{locationName}}',
        '{{taskName}}',
        '{{taskType}}',
        '{{taskId}}',
        '{{taskAction}}',
        '{{taskActionPast}}',
        '{{actorFullname}}',
        '{{actorEmail}}',
        '{{actorDisplayName}}',
        '{{senderFullname}}',
        '{{senderEmail}}',
        '{{note}}',
      ],
    };
  },
  computed: {
    isEditMode() {
      return !!this.form._id;
    },
  },
  created() {
    if (this.template) {
      this.form = {
        ...this.form,
        ...JSON.parse(JSON.stringify(this.template)),
      };
    }
  },
  methods: {
    setActiveEditorTarget(target) {
      this.activeEditorTarget = target;
      if (target === 'subject') {
        this.captureSubjectCursor();
      }
    },
    captureSubjectCursor() {
      const inputElement = this.$refs.subjectInput?.$el?.querySelector('input');
      if (!inputElement) {
        return;
      }

      this.subjectCursorPosition = inputElement.selectionStart || 0;
    },
    insertIntoSubject(placeholder) {
      const currentValue = this.form.subjectTemplate || '';
      const insertPosition = Math.max(
        0,
        Math.min(this.subjectCursorPosition, currentValue.length)
      );

      this.form.subjectTemplate =
        currentValue.slice(0, insertPosition) +
        placeholder +
        currentValue.slice(insertPosition);

      const nextCursorPosition = insertPosition + placeholder.length;
      this.subjectCursorPosition = nextCursorPosition;

      this.$nextTick(() => {
        const inputElement =
          this.$refs.subjectInput?.$el?.querySelector('input');
        if (!inputElement) {
          return;
        }

        inputElement.focus();
        inputElement.setSelectionRange(nextCursorPosition, nextCursorPosition);
      });
    },
    insertIntoBody(placeholder) {
      this.$refs.templateBodyEditor?.insertTextAtCursor(placeholder);
    },
    insertPlaceholder(placeholder) {
      if (this.activeEditorTarget === 'subject') {
        this.insertIntoSubject(placeholder);
        return;
      }

      this.insertIntoBody(placeholder);
      this.activeEditorTarget = 'body';
    },
    hasBodyContent(html) {
      if (!html) {
        return false;
      }
      const plain = String(html)
        .replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '')
        .replace(/<script[^>]*>[\s\S]*?<\/script>/gi, '')
        .replace(/<[^>]+>/g, ' ')
        .replace(/&nbsp;/gi, ' ')
        .replace(/\s+/g, ' ')
        .trim();
      return plain.length > 0;
    },
    save() {
      if (
        !this.form.name ||
        !this.form.subjectTemplate ||
        !this.hasBodyContent(this.form.bodyTemplate)
      ) {
        this.$buefy.snackbar.open({
          message: this.$t('admin_page.template_required_fields'),
          type: 'is-danger',
        });
        return;
      }

      this.$emit('save', {
        ...this.form,
        placeholders: this.recommendedPlaceholders.map((item) =>
          item.replace(/[{}]/g, '')
        ),
      });
    },
  },
};
</script>

<style scoped>
.nt-editor-modal-card {
  width: min(900px, calc(100vw - 3rem));
  max-width: 900px;
}

.nt-placeholders {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.nt-placeholder-tag {
  font-family: monospace;
  cursor: pointer;
}
</style>
